import {useMemo, useState, useEffect} from "react";
import {GridColDef, GridFilterModel, GridSortModel, GRID_CHECKBOX_SELECTION_COL_DEF} from "@mui/x-data-grid-pro";
import {useGetTimeInTraining, TimeInTrainingDto} from "@/app2/shared/data";
import {useTeamProgressContext} from "../../context/TeamProgressContext";
import {useManageTeamProgressReport} from "../../hooks";
import {useNavigate} from "react-router-dom";
import {AdminTable} from "@/app2/shared/components/AdminTable";
import {useTeamProgressColumns} from "../../utils/columns";
import {gridFilterModelToApiFilters} from "../../utils/filters";
import dayjs from "dayjs";
import type {GetCohortSettingsResponse} from "@/app2/shared/data/gen";
import useCustomDataGrid from "@/app2/shared/hooks/useCustomDataGrid";

interface TimeInTrainingDataGridProps {
	organization?: GetCohortSettingsResponse;
}

/**
 * Time in Training Data Grid
 * Matches legacy implementation exactly
 */
export function TimeInTrainingDataGrid({organization}: TimeInTrainingDataGridProps) {
	const {filters, updateSectionState} = useTeamProgressContext();
	const navigate = useNavigate();
	const commonColumns = useTeamProgressColumns(organization);

	const {
		api: {
			assignFilters,
		}} = useCustomDataGrid<TimeInTrainingDto>({});

	const [sortModel, setSortModel] = useState<GridSortModel>([
		{field: "firstname", sort: "asc"}
	]);
	const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

	// Export functionality
	const {initDownload, isExporting} = useManageTeamProgressReport<TimeInTrainingDto>("timeInTraining", filterModel, sortModel);

	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);

	const handleFilterModelChange = (newFilterModel: GridFilterModel) => setFilterModel(newFilterModel);

	// Reset page to 0 when filters change
	useEffect(() => {
		setPage(0);
	}, [filters.dateFrom, filters.dateTo, filters.archivedUsers]);
	useEffect(() => {
		setPage(0);
	}, [filterModel]);

	const {data, isLoading} = useGetTimeInTraining({
		dateFrom: filters.dateFrom,
		dateTo: filters.dateTo,
		archivedUsers: filters.archivedUsers,
		page: page + 1,
		pageSize: pageSize,
		order: sortModel[0]?.sort || "asc",
		orderBy: sortModel[0]?.field || "firstname",
		filters: gridFilterModelToApiFilters(filterModel),
	});

	useEffect(() => {
		updateSectionState(isLoading, "dataGrid");
	}, [isLoading, updateSectionState]);

	const columns: GridColDef<TimeInTrainingDto>[] = useMemo(
		() => {
			const getColumns = () => {
				const baseColumns: GridColDef<TimeInTrainingDto>[] = [
					{
						...GRID_CHECKBOX_SELECTION_COL_DEF,
						type: "string" as const,
						hideable: false
					},
					commonColumns.firstname,
					commonColumns.lastname,
					{
						field: "completedLessons",
						headerName: "Completed Lessons",
						flex: 1,
						type: "number" as const
					},
					{
						field: "totalWatchTime",
						headerName: "Total Watch Time",
						flex: 1,
						type: "string" as const,
						filterable: false
					},
					commonColumns.email,
					commonColumns.phone,
					commonColumns.supervisor,
					commonColumns.division,
					commonColumns.status,
					commonColumns.jobCategory,
					commonColumns.employeeCode
				];

				// Add custom field columns from commonColumns when enabled
				if (organization?.isActiveCustomField1) baseColumns.push(commonColumns.customFieldValue1);
				if (organization?.isActiveCustomField2) baseColumns.push(commonColumns.customFieldValue2);
				if (organization?.isActiveCustomField3) baseColumns.push(commonColumns.customFieldValue3);

				return baseColumns;
			};

			return getColumns();
		},
		[commonColumns, organization]
	);

	const getFilterDescription = (): string => {
		const {dateRangeType, dateFrom, dateTo} = filters;

		switch (dateRangeType) {
			case "lastWeek":
				return "VIEWING STATS FROM THE PAST WEEK";
			case "lastMonth":
				return "VIEWING STATS FROM THE PAST MONTH";
			case "lastYear":
				return "VIEWING STATS FROM THE PAST YEAR";
			case "customDateRange": {
				const fromDate = dayjs(dateFrom).format("MM/DD/YYYY");
				const toDate = dayjs(dateTo).format("MM/DD/YYYY");
				return `VIEWING STATS ${fromDate} - ${toDate}`;
			}
			default:
				return "VIEWING STATS FROM ALL TIME";
		}
	};

	const handleRowClick = (row: TimeInTrainingDto) => {
		navigate(`/admin/users/${row.userId}`);
	};

	return (
		<AdminTable
			rows={data?.data || []}
			columns={columns.map(e => assignFilters(e as any))}
			totalCount={data?.totalCount || 0}
			loading={isLoading}
			page={page}
			pageSize={pageSize}
			pageSizeOptions={[10, 25, 50, 100, 200]}
			onPageChange={setPage}
			onPageSizeChange={setPageSize}
			sortModel={sortModel}
			onSortModelChange={setSortModel}
			filterModel={filterModel}
			onFilterModelChange={handleFilterModelChange}
			onRowClick={handleRowClick}
			getRowId={(row) => row.userId.toString()}
			toolbarText={getFilterDescription()}
			checkboxSelection={false}
			enableAdvancedFilters={true}
			enableColumnFilter={true}
			disableColumnResize={false}
			autoHeight
			initialColumnVisibility={{
				email: false,
				phone: false,
				status: false,
				jobCategory: false,
				streak: false,
				customFieldValue1: false,
				customFieldValue2: false,
				customFieldValue3: false
			}}
			onExport={initDownload}
			exportDisabled={isExporting}
			exportButtonText={isExporting ? "Exporting..." : "Export"}
		/>
	);
}
